<template>
  <div
    class="cf-card cf-card--clickable cf-flex cf-flex--column cf-clear-padding cf-max-width-500 cf-cursor-pointer"
    @click="offerDetailsClickHandler"
  >
    <img
      v-if="offer.couponDetailsImageUrl"
      class="cf-image"
      :src="offer.couponDetailsImageUrl"
      onerror="this.src='/images/image-not-found.png';"
    >

    <div class="cf-flex cf-flex--column cf-flex--gap-12 cf-padding">
      <h4 class="cf-title cf-title--4">
        {{ offer.title }}
      </h4>

      <p class="cf-subtitle cf-subtitle--2">
        {{ offer.description }}
      </p>

      <div class="cf-flex cf-flex--column cf-flex--gap-12 cf-flex--align-center">
        <button
          v-if="isStartOrderFromOfferEnabled"
          type="button"
          class="cf-button cf-button--primary cf-button--lg"
          @click.stop="startOrderClickHandler"
        >
          {{ startOrderContent }}
        </button>

        <button
          v-if="isInStoreRedemptionEnabled"
          type="button"
          class="cf-button cf-button--tertiary cf-button--lg"
          :disabled="isDisabled"
          @click.stop="redeemOfferClickHandler"
        >
          {{ redeemCtaContent }}
        </button>

        <a
          v-if="offer.webRedemptionUrl"
          class="cf-button cf-button--primary cf-button--lg"
          :href="offer.webRedemptionUrl"
          :disabled="isDisabled"
          target="_blank"
          @click.stop
        >
          {{ content.Loyalty.metadata.buttonCta }}
        </a>
      </div>

      <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-space-between">
        <p
          v-if="offer.endDate"
          class="cf-subtitle cf-subtitle--3 cf-subtitle--heavy cf-text-italics"
        >
          Expires {{ $moment(offer.endDate, 'M/D/YY') }}
        </p>

        <a
          v-if="offer.finePrintUrl"
          class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy cf-cursor-pointer"
          :href="offer.finePrintUrl"
          target="_blank"
        >
          Terms & Conditions
        </a>
      </div>
    </div>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';


  export default {
    name: 'OfferAllocationCard',

    props: {
      offer: {
        type: Object,
        required: true
      },

      isOfferDisabled: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        previousRoute: ''
      };
    },

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('session', ['currentUser']),
      ...mapGetters('merchant', ['merchant', 'offerRedemptionFlow']),

      isInStoreRedemptionEnabled() {
        const hasPosIntegratedAppRedemptionCode = this.offer.redemptionChannels.some(channel => channel === 'PosIntegratedAppRedemptionCode');

        // Check new merchant configuration first
        if (this.offerRedemptionFlow === 'None') {
          return false;
        }

        if (this.offerRedemptionFlow && ['ActivateOfferKeyForSingleScan', 'GenerateRedemptionCodeForSingleScan', 'GenerateCheckinTokenForDisplay'].includes(this.offerRedemptionFlow)) {
          return hasPosIntegratedAppRedemptionCode;
        }

        // Fall back to legacy configuration for backward compatibility
        if (this.merchant.options.loyaltyOptions?.inStoreLoyaltyOptions?.disableInStoreRedemption) {
          return !this.merchant.options.loyaltyOptions.inStoreLoyaltyOptions.disableInStoreRedemption;
        }

        return hasPosIntegratedAppRedemptionCode;
      },

      scanAndPayDisabled() {
        return this.merchant.features['disable-scan-and-pay'];
      },

      isDisabled() {
        /** this.isOfferDisabled acts as an override */
        return this.isOfferDisabled || !this.currentUser.allowInStoreOfferRedemption;
      },

      isStartOrderFromOfferEnabled() {
        // Use new configuration path first
        if (this.merchant.options?.offersOptions?.startOrderFromOfferEnabled !== undefined) {
          return this.merchant.options.offersOptions.startOrderFromOfferEnabled;
        }

        // Fall back to deprecated path for backward compatibility
        return this.merchant.options.loyaltyOptions?.inStoreLoyaltyOptions?.startOrderFromOffer || false;
      },

      startOrderContent() {
        return this.content.LoyaltyOfferRedemption.metadata.startMobileOrderCta;
      },

      redeemCtaContent() {
        return this.content.Loyalty.metadata.availableOfferInStoreRedemptionCta;
      }
    },

    mounted() {
      this.mountedHandler();
    },

    methods: {
      mountedHandler() {
        this.previousRoute = this.$route.query?.origin;
      },

      startOrderClickHandler() {
        const routerPayload = { name: 'orderMenu', params: { merchantId: this.$route.params.merchantId } };

        this.$router.push(routerPayload);
      },

      redeemOfferClickHandler() {
        if (this.offerRedemptionFlow === 'GenerateCheckinTokenForDisplay') {
          const modalPayload = { offer: this.offer };
          this.$openModal('offerInStoreRedemption', modalPayload);
        }
        else {
          let routeName = this.previousRoute;
          if (!routeName) {
            routeName = this.scanAndPayDisabled ? 'scanOnly' : 'scanToPay';
          }

          this.$router.push({
            name: routeName,
            query: {
              offerAllocationPublicId: this.offer.offerAllocationPublicId
            }
          });
        }
      },

      offerDetailsClickHandler() {
        const modalPayload = { offer: this.offer };

        this.$openModal('offerDetails', modalPayload);
      }
    }
  };
</script>
